// src/pages/review/WriteReviewPage.jsx
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useBooks } from '../../hooks/useBooks';
import { useAuth } from '../../hooks/useAuth';
import { useReviews } from '../../contexts/ReviewContext';
import StarRating from 'react-stars';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const WriteReviewPage = () => {
  const { id } = useParams();
  const { books, loading: booksLoading } = useBooks();
  const { currentUser } = useAuth();
  const { addReview } = useReviews();

  const book = books.find(b => b.id === id);

  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  if (booksLoading) return <LoadingSpinner />;
  if (!book) return <div className="container">Book not found</div>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setMessage('Please log in to submit a review.');
      return;
    }
    if (!title.trim() || !text.trim()) {
      setMessage('Title and review text are required.');
      return;
    }

    setSubmitting(true);
    try {
      await addReview(id, {
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email.split('@')[0],
        rating,
        title: title.trim(),
        text: text.trim()
      });
      setMessage('Thank you! Your review has been submitted.');
      setTitle('');
      setText('');
      setRating(5);
    } catch (err) {
      setMessage('Failed to submit review.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="write-review-page">
      <div className="container">
        <Link to={`/book/${id}`} className="back-link">← Back to product</Link>

        <h1>Review this product</h1>
        <p className="subtitle">Share your thoughts with other customers</p>

        {/* Product preview - like Amazon */}
        <div className="product-preview">
          <img
            src={book.imageUrl || '/placeholder-book.jpg'}
            alt={book.title}
            className="preview-image"
          />
          <div className="preview-info">
            <h2 className="preview-title">{book.title}</h2>
            <p className="preview-author">by {book.author}</p>
          </div>
        </div>

        {/* Form - Amazon style */}
        <form onSubmit={handleSubmit} className="review-form">
          <div className="form-group">
            <label className="form-label">How was the item?</label>
            <StarRating
              count={5}
              value={rating}
              onChange={setRating}
              size={40}
              color2="#FFA41C"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Title your review (required)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's most important to know?"
              required
              className="title-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Write your review</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What did you like or dislike? What did you use this product for?"
              rows="10"
              required
              className="review-textarea"
            />
          </div>

          {message && (
            <p className={`form-message ${message.includes('Thank') ? 'success' : 'error'}`}>
              {message}
            </p>
          )}

          <button type="submit" disabled={submitting} className="submit-btn">
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WriteReviewPage;